import React, { useEffect, useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Dashboard from "./components/Dashboard";
import { Spinner, Modal, Button } from "react-bootstrap";
import LoginPage from "./components/LoginPage";
import NavbarComponent from "./components/NavbarComponent";
import { positions, Provider } from "react-alert";
import AlertTemplate from "react-alert-template-basic";
import Subscription from "./components/SubscriptionPage/Subscription";
import SubscriptionNew from "./components/SubscriptionPage/SubscriptionNew";
import SignUpLab from "./components/signupClient/SignUpLab";
import SignUpLIS from "./components/signupClient/SignUpLIS";
import SignUpAdmin from "./components/signupClient/SignUpAdmin";
import MaintenancePage from "../src/components/maintenance";
import ClientsTableView from "./components/SubscriptionPage/tableView";
import VizAppDashboard from "./components/DashboardVizapp";
import ReleaseTrackingPage from "./components/ReleaseTrackingPage/ReleaseTrackingPage";
import PersonaFetchPage from "./components/ReleaseTrackingPage/PersonaFetch/PersonaFetchPage";
import regenerateToken from "./lib/regenerateToken";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
});

const App = () => {
  // RECORD DATE
  // var datetime =  new Date().toLocaleString().replace(',','')
  // let onlyDate = datetime.split(" ")[0]
  // let onlyTime = datetime.split(" ")[1]

  // console.log("date and time: ", onlyDate, ",",onlyTime)

  // useEffect(() => {
  //   window.addEventListener("beforeunload", () => localStorage.removeItem('loginDetails'));
  // }, []);

  // DETECT CLOSING AND REFRESHING OF WEBPAGE
  // useEffect(() => {
  //   return () => {

  //      window.addEventListener("beforeunload", function(e) {

  //     let confirmationMessage = "o/";

  //     (e || window.event).returnValue = confirmationMessage; //Gecko + IE

  //     console.log("logout !");
  //     localStorage.setItem("myLoginDetails", JSON.stringify(""))
  //     localStorage.removeItem("loginDetails")
  //     localStorage.removeItem("homepageData")

  //     return confirmationMessage; //Webkit, Safari, Chrome

  //   });
  //  }

  // });

  // react alert config
  const options = {
    timeout: 5000,
    // position: positions.BOTTOM_CENTER
  };

  const [loadingStatus, setLoadingStatus] = useState(true);

  const cacheImages = async (imagesToLoad) => {
    const promises = await imagesToLoad.map((src) => {
      return new Promise(function (resolve, reject) {
        const img = new Image();

        img.src = src;
        img.onload = resolve();
        img.onerror = reject();
      });
    });
    await Promise.all(promises);
    setLoadingStatus(false);
  };

  useEffect(() => {
    // loading all the images first
    // const allImages = collectionOfAllLinks

    const allImages = [
      "https://niroggyan.s3.ap-south-1.amazonaws.com/React-Billing-Dashboard-files/WelcomeScreen+Files/welcomeScreenSvg.svg",
    ];

    //console.log("all Links\n", allImages)

    cacheImages(allImages);
  }, []);

  const loginData = JSON.parse(localStorage.getItem("loginDetails"));

  const AUTO_LOGOUT_MS = 8 * 60 * 60 * 1000; // 8 hours (full workday — prevents data loss during long sessions)
  const WARNING_BEFORE_MS = 5 * 60 * 1000; // show warning 5 minutes before logout
  const WARNING_THRESHOLD_MS = AUTO_LOGOUT_MS - WARNING_BEFORE_MS;

  const performLogout = useCallback(() => {
    try {
      localStorage.clear();
      localStorage.setItem("myLoginDetails", JSON.stringify(""));
      localStorage.setItem("clientPreference", JSON.stringify("none"));
      localStorage.setItem(
        "logoUrl",
        JSON.stringify(
          "https://niroggyan.s3.ap-south-1.amazonaws.com/new-billing-dashboard-files/logos/niroggyan-logo.svg"
        )
      );
    } catch (_) {}
    window.dispatchEvent(new Event("storage"));
    window.location.replace("/login");
  }, []);

  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-logout after 8 hours; show warning popup 5 minutes before
  useEffect(() => {
    const checkExpiry = () => {
      const raw = localStorage.getItem("loginTimestamp");
      if (!raw) {
        setShowSessionWarning(false);
        return;
      }
      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp)) return;
      const elapsed = Date.now() - timestamp;
      if (elapsed >= AUTO_LOGOUT_MS) {
        setShowSessionWarning(false);
        performLogout();
        return;
      }
      if (elapsed >= WARNING_THRESHOLD_MS) {
        setShowSessionWarning(true);
      } else {
        setShowSessionWarning(false);
      }
    };

    checkExpiry();
    const id = setInterval(checkExpiry, 60 * 1000);
    return () => clearInterval(id);
  }, [performLogout]);

  // Silently refresh idTokenBilling every 50 minutes (before AWS Cognito's 1-hour expiry).
  // KEY FIX: On every app mount/page refresh, we first check how long ago the token was
  // last refreshed. If >= 50 minutes have passed, we refresh IMMEDIATELY before starting
  // the interval — so a browser refresh never leaves the token expired for ~50 extra minutes.
  useEffect(() => {
    const loginTimestamp = localStorage.getItem("loginTimestamp");
    // Only run if the user is actually logged in
    if (!loginTimestamp) return;

    const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes

    const doRefresh = async () => {
      try {
        await regenerateToken();
        // Save the exact time of this refresh so we can calculate elapsed time on next mount
        localStorage.setItem("lastTokenRefresh", String(Date.now()));
        // Also reset the 8-hour session window
        localStorage.setItem("loginTimestamp", String(Date.now()));
        console.log("[Auth] Token refreshed at", new Date().toLocaleTimeString());
      } catch (e) {
        console.warn("[Auth] Scheduled token refresh failed:", e.message);
      }
    };

    // On startup: check if the token is already stale (e.g. after a page refresh or new tab).
    // If 50+ minutes have passed since the last refresh (or since login if never refreshed),
    // fire an immediate refresh so the token is valid right now.
    const lastRefresh = Number(localStorage.getItem("lastTokenRefresh") || loginTimestamp);
    const elapsedSinceLastRefresh = Date.now() - lastRefresh;

    let startupTimeout = null;
    if (elapsedSinceLastRefresh >= REFRESH_INTERVAL_MS) {
      // Token is already >= 50 min old — refresh immediately on mount
      console.log("[Auth] Token stale on startup, refreshing immediately...");
      doRefresh();
    } else {
      // Schedule the first refresh for when the remaining time elapses
      const remainingMs = REFRESH_INTERVAL_MS - elapsedSinceLastRefresh;
      console.log(
        `[Auth] Next token refresh in ${Math.round(remainingMs / 60000)} min(s).`
      );
      startupTimeout = setTimeout(async () => {
        await doRefresh();
      }, remainingMs);
    }

    // After the first refresh (immediate or scheduled), keep refreshing every 50 minutes
    const refreshInterval = setInterval(doRefresh, REFRESH_INTERVAL_MS);

    return () => {
      if (startupTimeout) clearTimeout(startupTimeout);
      clearInterval(refreshInterval);
    };
  }, []);

  const ProtectedRoutes = () => {
    const loginData = JSON.parse(localStorage.getItem("loginDetails"));
    return loginData ? <Outlet /> : <Navigate to="/login" />;
  };

  return (
    <QueryClientProvider client={queryClient}>
      {loadingStatus ? (
        <Spinner
          style={{ position: "absolute", top: "50%", left: "50%" }}
          id="loadingSpinnerCss"
          animation="border"
          role="status"
        />
      ) : (
        <>
          <NavbarComponent />
          <Provider template={AlertTemplate} {...options}>
            {/* Session expiry warning: show before auto-logout so user can log out or be informed */}
            {showSessionWarning && loginData && (
              <Modal
                show={true}
                backdrop="static"
                keyboard={false}
                centered
                aria-labelledby="session-warning-title"
                aria-describedby="session-warning-desc"
              >
                <Modal.Header>
                  <Modal.Title id="session-warning-title">Session expiring</Modal.Title>
                </Modal.Header>
                <Modal.Body id="session-warning-desc">
                  Your session will expire in a few minutes due to inactivity. You will be automatically logged out.
                  Click the button below to log out now if you are done.
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="success"
                    disabled={isRefreshing}
                    onClick={async () => {
                      setIsRefreshing(true);
                      try {
                        await regenerateToken();
                        localStorage.setItem("loginTimestamp", String(Date.now()));
                        setShowSessionWarning(false);
                      } catch (e) {
                        console.error("[Auth] Keep-signed-in refresh failed:", e.message);
                      } finally {
                        setIsRefreshing(false);
                      }
                    }}
                  >
                    {isRefreshing ? "Refreshing..." : "Keep me Signed In"}
                  </Button>
                  <Button variant="primary" onClick={performLogout}>
                    Log out
                  </Button>
                </Modal.Footer>
              </Modal>
            )}
            <div className="container-fluid p-3">
              <Router>
                <Routes>
                  <Route element={<ProtectedRoutes />}>
                    <Route
                      exact
                      path="/"
                      element={loginData ? <VizAppDashboard /> : <LoginPage />}
                    />

                    <Route
                      exact
                      path="/subscription"
                      element={<Subscription />}
                    />
                    <Route
                      exact
                      path="/client-subscription"
                      element={<SubscriptionNew />}
                    />
                    <Route
                      exact
                      path="/new-billing-dashboard"
                      element={<VizAppDashboard />}
                    />
                    <Route
                      exact
                      path="/new-client/lab"
                      element={<SignUpLab />}
                    />
                    <Route
                      exact
                      path="/new-client/lis"
                      element={<SignUpLIS />}
                    />
                    <Route
                      exact
                      path="/new-client/admin"
                      element={<SignUpAdmin />}
                    />
                    <Route
                      exact
                      path="/maintenance"
                      element={<MaintenancePage />}
                    />
                    <Route
                      exact
                      path="/client-Data"
                      element={<ClientsTableView />}
                    />
                    <Route
                      exact
                      path="/release-tracking"
                      element={<ReleaseTrackingPage />}
                    />
                    <Route
                      exact
                      path="/persona-fetch"
                      element={<PersonaFetchPage />}
                    />
                  </Route>
                  <Route exact path="/login" element={<LoginPage />} />
                  {/* <Route path="*" element={<NotFound/>}/> */}
                </Routes>
              </Router>
            </div>
          </Provider>
        </>
      )}
    </QueryClientProvider>
  );
};

export default App;
